/**
 * La pestaña Reescritura del panel de Word: los datos que da el motor (`GET /reescribir`) y las frases que se pintan.
 * Sin React y sin red, para poder probarlo solo.
 */

export type PasadaDelPlan = {
  n: number; titulo: string; estado: "pendiente" | "hecha" | "saltada"; porque?: string; prioridad?: string;
  /** Apagada por lo que el autor dijo en el perfil de su firma que no quería: qué es («poda», «estilo»). */
  contra?: string[];
  /** No escribe texto: deja indicado dónde tiene que escribir el autor. */
  indicacion?: boolean;
  /** Lo que el autor hizo en Word con sus cambios, si ya se leyó. */
  leida?: LoLeido;
  /** Los rechazó todos: está hecha y APARTADA (no vuelve sola a la cola). */
  rechazada?: boolean;
  /** Lo que hizo no está en lo último del libro: su versión (`version`) se tiró (`version_esta: false`) o lo último no sale de ella. */
  perdida?: boolean; version?: string; version_esta?: boolean;
};
/** Lo leído de una versión ya decidida: cuántos cambios se aceptaron, se retocaron o se rechazaron, y en cuántos el autor escribió otra cosa. */
export type LoLeido = { cambios: number; aceptados: number; retocados: number; rechazados: number; tuyos: number; no_evaluables?: number; dudosa?: boolean };
/** Lo leído con una versión anterior del programa en un documento que leía mal (14-oct-2026): no se enseñan sus cifras. */
export const LEIDO_MAL = "Lo que decidiste se leyó con una versión anterior del programa, que en este documento contaba mal: sus cifras no valen.";
export type FaseDelPlan = { n: number; indice: number; nombre: string; pasadas: PasadaDelPlan[] };
export type PlanDeReescritura = {
  aprobado: boolean; fecha: string | null;
  /** El Word del plan, si sigue en la carpeta del libro: su nombre. */
  documento: string | null;
  fases: FaseDelPlan[]; pasadas: number; pendientes: number; hechas: number; saltadas: number;
};
/** La fase que toca, tal como la cuenta el motor. `fase: null` si no queda ninguna. */
export type FaseQueToca = {
  fase: number | null; indice?: number; de?: number; area?: string; pasadas?: { n: number; titulo: string }[] | number; codigo?: string;
  /** Alguna de sus pasadas llama al modelo: hace falta la llave. */
  necesita_llave?: boolean;
  /** «capitulos»: los capítulos de alguna pasada no están en el documento. */
  no_se_puede?: string;
} | null;
/** Lo que costará la fase, dicho antes de pulsar. `dinero: null` si no hay de dónde sacar la cifra. */
export type Cuenta = { palabras: number; llamadas: number; minutos: number; dinero: { usd: number; base: "medido" | "precios" | "sin_modelo" } | null; medido: boolean };
export type ResultadoDeFase = {
  fichero?: string; n?: number; cambios?: number; abierto?: boolean; coste?: number;
  pasadas?: { n: number; titulo: string; cambios: number }[]; sin_cambios?: { n: number; titulo: string }[];
  /** Cambios que tocaban títulos o separadores de escena: no se han escrito. */
  fuera_del_lote?: number;
  /** Partes que el modelo devolvió cortadas o vacías: se han dejado como estaban. */
  sin_tocar?: unknown[];
  /** Operaciones del plan que no encontraron su escena. */
  sin_sitio?: { pasada: number; n: number; motivo: string }[];
  /** Cambios de varias pasadas sobre el mismo párrafo: llevan el revisor de la fase. */
  compartidos?: number; revisor_de_la_fase?: string;
  /** Llamadas que se cortaron a medias y hubo que repetir: lo que costaran no está en `coste`. */
  cortadas?: number;
};
export type Trabajo = {
  id: string; estado: "en_curso" | "hecho" | "fallido" | "interrumpido"; titulo: string;
  progreso: { hecho: number; total: number; fase?: string } | null; pasos: { titulo: string; estado: string }[] | null;
  error: string | null; resultado: ResultadoDeFase | null; inicio: string; fin: string | null;
};
export type Reescritura = {
  hay: true; fichero: string;
  /** El documento del que sale la cadena de versiones: de él es el plan. */
  origen: string;
  plan: PlanDeReescritura | null;
  /** Hay un plan en la carpeta, pero se hizo con este otro documento. */
  plan_de_otro?: string;
  fase: FaseQueToca;
  /** No se ha podido leer el perfil de la firma del libro. */
  sin_preferencias?: boolean;
  /** Hay algo en marcha que lee o escribe el plan: su frase. */
  ocupado?: string | null;
  /** La cadena de versiones: cuál es la última y si es la que está abierta. Una fase se escribe sobre la última. */
  cadena?: { ultima: string | null; es_la_ultima: boolean; versiones: number };
  hay_motor?: boolean; llave?: boolean;
  /** Cambios sin aceptar ni rechazar en el documento abierto; `null` si no se ha mirado o no se puede leer. */
  marcas?: number | null;
  cuenta?: Cuenta | null;
  /** La fase de este libro en marcha, o la última que se lanzó. */
  trabajo?: Trabajo | null;
  /** Si el documento abierto es una versión que dejó una fase: si ya se leyó lo que el autor decidió en ella, y qué salió. */
  lectura?: { n: number; fase: number | null; leida: boolean; dudosa?: boolean; cuenta?: LoLeido & { no_evaluables?: number; palabras_no_tuyas?: number } };
};
export type SinReescritura = { hay: false; motivo: string; fichero: string | null };
/** `rehacer`: recuperar una pasada apartada (hecha, y rechazada entera en Word) para que se haga otra vez. */
export type Orden = { accion: "aprobar" | "desaprobar" } | { accion: "saltar" | "recuperar" | "rehacer"; n: number };

const veces = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;

/** En qué punto está la pestaña: de aquí sale qué se enseña y qué botón lleva el relleno. */
export type Momento = "freno" | "sin_plan" | "de_otro" | "por_aprobar" | "aprobado" | "terminado";
export function momentoDe(d: Reescritura | SinReescritura): Momento {
  if (!d.hay) return "freno";
  if (!d.plan) return d.plan_de_otro ? "de_otro" : "sin_plan";
  if (d.plan.pendientes === 0) return "terminado";
  return d.plan.aprobado ? "aprobado" : "por_aprobar";
}

/** El botón que abre la novela construida desde el manuscrito por escenas: su frase lo nombra igual. */
export const ABRIR_LA_NOVELA = "Abrir la novela";

/** Por qué aquí no hay nada que reescribir, dicho con lo que hay que hacer. */
export function porQueNo(d: SinReescritura): string {
  const f = d.fichero ? `«${d.fichero}»` : "Este documento";
  // Con el manuscrito por escenas sale junto al botón de abrir la novela (`queAbrir`), y manda a él (24-oct-2026).
  if (d.motivo === "por_escenas") {
    return `${f} es el manuscrito por escenas. La reescritura se hace sobre la novela construida: lo que cambies aquí no llegaría a ella. Si ya la has construido, pulsa «${ABRIR_LA_NOVELA}». Si no, constrúyela en la pestaña Escritura.`;
  }
  if (d.motivo === "es_generado") return `${f} lo ha escrito HumanInk (un informe, un diagnóstico o un plan). Abre tu novela en Word y vuelve a esta pestaña.`;
  return "Guarda el documento en este ordenador y vuelve a esta pestaña: el plan de reescritura es de un documento guardado.";
}

export const FRASE_SIN_PLAN = "Este libro todavía no tiene plan de reescritura. Lo escribe el diagnóstico completo, con todos los informes delante: pídelo en la pestaña Diagnóstico.";
export function fraseDePlanDeOtro(d: Reescritura): string {
  return d.plan_de_otro
    ? `El plan de reescritura que hay en esta carpeta se hizo con «${d.plan_de_otro}», no con este documento. Abre «${d.plan_de_otro}» para verlo, o pide el diagnóstico de este en la pestaña Diagnóstico.`
    : "";
}
/** Con una versión abierta (una corrección, una reescritura): de qué libro es el plan. */
export function fraseDelOrigen(d: Reescritura): string {
  return d.plan && d.origen && d.origen !== d.fichero ? `Estás en una versión de «${d.origen}»: el plan es el de ese libro.` : "";
}

/** El plan en una línea: cuánto es y cómo va. */
export function resumenDelPlan(p: PlanDeReescritura): string {
  const partes = [
    p.pendientes ? `${p.pendientes} por hacer` : "",
    p.hechas ? veces(p.hechas, "hecha", "hechas") : "",
    p.saltadas ? (p.saltadas === 1 ? "1 que no se hará" : `${p.saltadas} que no se harán`) : "",
  ].filter(Boolean);
  const cola = partes.length > 1 ? `${partes.slice(0, -1).join(", ")} y ${partes[partes.length - 1]}` : partes[0] ?? "";
  return `${veces(p.fases.length, "fase", "fases")} y ${veces(p.pasadas, "pasada", "pasadas")}${cola ? `: ${cola}` : ""}.`;
}

/** Lo que se le dice al autor sobre aprobar, según dónde esté. */
export function fraseDeAprobar(d: Reescritura): string {
  const m = momentoDe(d);
  if (m === "por_aprobar") return "Desmarca las pasadas que no quieras y aprueba el plan. Hasta que lo apruebes no se reescribe nada.";
  if (m === "aprobado") return "Has aprobado este plan. Puedes seguir desmarcando pasadas: lo que ya está hecho no cambia.";
  if (m === "terminado") return d.plan!.hechas > 0 ? "Plan hecho: no queda ninguna pasada por hacer. Lo siguiente es la Corrección." : "No queda ninguna pasada por hacer: las has desmarcado todas. Marca alguna si quieres reescribir.";
  return "";
}

/** ¿Se puede marcar o desmarcar esta pasada? Lo hecho no cambia, y lo apagado por el perfil se cambia en el perfil. */
export function seCambia(x: PasadaDelPlan, d: Reescritura): boolean {
  return x.estado !== "hecha" && !(x.contra?.length) && !d.ocupado;
}
/** Lo que hace el clic en su casilla: desmarcar una pendiente es no hacerla; marcar una desmarcada, recuperarla. */
export const ordenDe = (x: PasadaDelPlan): Orden => ({ accion: x.estado === "saltada" ? "recuperar" : "saltar", n: x.n });
/** La casilla está marcada si la pasada se hace o ya se hizo. */
export const marcada = (x: PasadaDelPlan) => x.estado !== "saltada";

const QUE_NO_QUIERE: Record<string, string> = { poda: "poda", estilo: "cambios de estilo" };
/** Lo que se dice junto a una pasada: que está hecha, que no se hará, o por qué está apagada. Vacío si no hay nada que decir. */
export function notaDePasada(x: PasadaDelPlan): string {
  if (x.estado === "hecha") {
    if (x.perdida) {
      const cual = x.version ? ` («${x.version}»)` : "";
      return x.version_esta
        ? `Lo que hizo no está en este documento: lo último del libro no sale de su versión${cual}. Recupera la pasada para que se haga otra vez.`
        : `Lo que hizo no está en este documento: su versión${cual} ya no está en la carpeta HumanInk del libro. Si la tiraste sin querer, devuélvela a esa carpeta; si no, recupera la pasada para que se haga otra vez.`;
    }
    if (x.rechazada) return "La rechazaste entera en Word: está apartada. Si quieres que se haga otra vez, recupérala.";
    return x.leida ? `Hecha. ${fraseDeLoLeido(x.leida)}` : "Hecha.";
  }
  if (x.contra?.length) {
    const que = x.contra.map((k) => QUE_NO_QUIERE[k] ?? k).join(" ni ");
    return `No se hará: en el perfil de la firma de este libro consta que no quieres ${que}.`;
  }
  if (x.estado === "saltada") return "No se hará.";
  if (x.indicacion) return "No escribe texto por ti: deja indicado dónde tienes que escribir tú.";
  return "";
}

/** Lo que el autor hizo con unos cambios, en una frase: «En Word aceptaste 12 de 15 cambios, retocaste 1 y rechazaste 2.» */
export function fraseDeLoLeido(l: LoLeido): string {
  if (l.dudosa) return LEIDO_MAL;
  if (!l.cambios) return "No tenía cambios que decidir.";
  const mas = [
    l.retocados ? `retocaste ${l.retocados}` : "",
    l.rechazados ? `rechazaste ${l.rechazados}` : "",
    l.tuyos ? `en ${l.tuyos} escribiste otra cosa` : "",
  ].filter(Boolean);
  const cola = mas.length > 1 ? `, ${mas.slice(0, -1).join(", ")} y ${mas[mas.length - 1]}` : mas.length ? ` y ${mas[0]}` : "";
  // Lo que no se ha podido leer se dice (14-oct-2026: «aceptaste 15 de 61 cambios», sin más, parecía que los otros 46 se
  // habían perdido o rechazado).
  const sinSaber = l.no_evaluables ? ` De ${l.no_evaluables} no se puede saber qué hiciste.` : "";
  return `En Word aceptaste ${l.aceptados} de ${veces(l.cambios, "cambio", "cambios")}${cola}.${sinSaber}`;
}
/** ¿Se puede recuperar esta pasada apartada? Con algo en marcha, no. */
export const seRecupera = (x: PasadaDelPlan, d: Reescritura) => x.estado === "hecha" && (x.rechazada === true || x.perdida === true) && !d.ocupado && !enMarcha(d.trabajo);
/** Las pasadas hechas cuya versión ya no está: mientras haya alguna, no se lanza otra fase encima. */
export const perdidasDe = (d: Reescritura) => (d.plan?.fases ?? []).flatMap((f) => f.pasadas).filter((x) => x.estado === "hecha" && x.perdida === true);

/** ¿Hay que leer lo que el autor decidió en la versión abierta? La dejó una fase, no se ha leído, y ya no le quedan cambios por decidir. */
export const hayQueLeer = (d: Reescritura) => !!d.lectura && !d.lectura.leida && d.marcas === 0 && !enMarcha(d.trabajo);
/**
 * Lo que se dice de la versión abierta cuando la dejó una fase: que falta decidir, que ya se puede leer, o lo que salió.
 * Vacío si el documento abierto no es una versión de fase.
 */
export function fraseDeLectura(d: Reescritura): string {
  const l = d.lectura;
  if (!l) return "";
  const cual = l.fase != null ? `la fase ${l.fase}` : "esta fase";
  if (l.leida && l.dudosa) return LEIDO_MAL;
  if (l.leida) return l.cuenta ? `Lo que decidiste con ${cual} ya está leído. ${fraseDeLoLeido(l.cuenta)} Es lo que cuenta en tu certificado.` : `Lo que decidiste con ${cual} ya está leído.`;
  if (d.marcas === 0) return `Ya has decidido los cambios de ${cual}. Léelos para que quede apuntado qué aceptaste: es lo que cuenta en tu certificado. (Al lanzar la fase siguiente se leen solos.)`;
  // Con una fase por lanzar, que quedan cambios por decidir ya lo dice su freno: no se dice dos veces.
  if (d.marcas != null && d.marcas > 0 && !hayFaseQueLanzar(d)) return `Esta versión la dejó ${cual} y le ${d.marcas === 1 ? "queda 1 cambio" : `quedan ${d.marcas} cambios`} por decidir. Hazlo en Word (Revisar → Aceptar o Rechazar) y guarda.`;
  return "";
}

/** Qué fase es la siguiente, y cuántas pasadas lleva. Vacío si no queda ninguna. */
export function fraseDeLoSiguiente(d: Reescritura): string {
  const f = d.fase;
  if (!d.plan || !f || f.fase == null) return "";
  const cuantas = Array.isArray(f.pasadas) ? f.pasadas.length : Number(f.pasadas) || 0;
  const cual = `la fase ${f.indice ?? f.fase}${f.de ? ` de ${f.de}` : ""}${f.area ? ` · ${f.area}` : ""}${cuantas ? ` (${veces(cuantas, "pasada", "pasadas")})` : ""}`;
  return d.plan.aprobado ? `Lo siguiente es ${cual}.` : `Cuando lo apruebes, lo primero será ${cual}.`;
}

// ── Ejecutar la fase que toca ─────────────────────────────────────────────────────────────────────

export const miles = (n: number) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
export const dinero = (x: number) => { const c = Math.round(100 * (Number(x) || 0)); return `${miles(Math.floor(c / 100))},${String(c % 100).padStart(2, "0")} $`; };
export const enMarcha = (t: Trabajo | null | undefined) => t?.estado === "en_curso";
/** ¿Hay una fase que lanzar? El plan aprobado y una fase con pasadas por hacer. */
export const hayFaseQueLanzar = (d: Reescritura) => !!d.plan?.aprobado && d.fase?.fase != null;

/**
 * Por qué ahora mismo no se puede lanzar la fase, o `null` si se puede. Lo dice el motor al intentarlo; aquí se dice
 * ANTES, para no ofrecer un botón que va a contestar que no. En el mismo orden que sus puertas.
 */
export function porQueNoSeLanza(d: Reescritura): string | null {
  if (d.sin_preferencias) return AVISO_SIN_PREFERENCIAS;
  if (d.hay_motor === false) return "Esta instalación de HumanInk todavía no sabe escribir cambios en un Word. Actualízala.";
  if (d.ocupado) return d.ocupado;
  if (d.cadena && !d.cadena.es_la_ultima) {
    return d.cadena.ultima
      ? `Lo último de este libro es «${d.cadena.ultima}». Ábrelo en Word y lanza la fase desde ahí: cada fase se escribe sobre lo que decidiste de la anterior.`
      : "No encuentro la última versión de este libro en su carpeta.";
  }
  const perdidas = perdidasDe(d);
  if (perdidas.length) {
    const una = perdidas.length === 1;
    const falta = perdidas.every((x) => x.version_esta !== true);
    return `Lo que ${una ? `hizo la pasada ${perdidas[0].n}` : `hicieron las pasadas ${perdidas.map((x) => x.n).join(", ")}`} no está en este documento. ${falta ? "Devuelve su versión a la carpeta HumanInk del libro o pulsa" : "Pulsa"} «Recuperar» en ${una ? "esa pasada" : "esas pasadas"} para que se haga${una ? "" : "n"} otra vez: hasta entonces no se reescribe encima.`;
  }
  if (d.marcas == null) return `No he podido leer «${d.fichero}». Guárdalo en Word y pulsa «Volver a mirar».`;
  if (d.marcas > 0) {
    return `«${d.fichero}» tiene ${veces(d.marcas, "cambio", "cambios")} sin aceptar ni rechazar. Decide en Word (Revisar → Aceptar o Rechazar), guarda y vuelve: una fase se escribe sobre un texto decidido.`;
  }
  if (d.fase?.no_se_puede === "capitulos") return "Los capítulos de alguna pasada de esta fase no coinciden con los de este documento. Pide otra vez el diagnóstico: el plan se hizo con otro texto.";
  if (d.fase?.necesita_llave && d.llave === false) return "Falta tu llave de OpenRouter: ponla en Ajustes.";
  return null;
}
/** Con otra versión abierta que no es la última: se ofrece abrirla. */
export const hayQueAbrirLaUltima = (d: Reescritura) => !!d.cadena && !d.cadena.es_la_ultima && !!d.cadena.ultima;

// ── El recorrido, en la propia pestaña (24-oct-2026) ─────────────────────────────────────────────────────────────────
// Backlog (Rais, 14-oct): la pestaña no decía qué documento hay que tener abierto, qué botón ejecuta una fase, qué Word
// aparece ni dónde. Cada momento decía lo suyo, y lo de después solo salía con una fase ya hecha.

/** El recorrido entero, en cuatro pasos. Sale en todos los momentos de la pestaña, y antes que el plan. */
export const PASOS_DEL_RECORRIDO = [
  "Abre en Word tu novela entera y trabaja con su última versión. Si escribes por escenas, es la novela que construyes en la pestaña Escritura.",
  "Aprueba el plan y pulsa «Reescribir la fase». Antes de gastar, te digo lo que cuesta.",
  "Al terminar, la versión nueva, con los cambios marcados, queda en la carpeta HumanInk del libro y se abre en Word. Tu documento no se toca.",
  "Decide allí con Revisar (Aceptar o Rechazar), guarda y sigue desde el panel de esa ventana.",
];
/** A la vista mientras el libro no lleva ninguna pasada hecha; después, plegado y a un clic. */
export const recorridoDesplegado = (d: Reescritura | SinReescritura) => !d.hay || !d.plan || d.plan.hechas === 0;

/**
 * El documento con el que se trabaja, si no es el abierto: la novela construida (con el manuscrito por escenas abierto)
 * o la última versión del libro. `null` si ya es el abierto, si no se sabe cuál es, o si ya lo ofrece otro sitio: con
 * una fase que lanzar, el freno de la fase lleva su «Abrir la última versión»; con una en marcha, lo que toca es esperar.
 * Con un informe de HumanInk abierto tampoco: abrir la novela desde ahí necesitaría una ruta nueva del motor.
 */
export function queAbrir(d: Reescritura | SinReescritura): "novela" | "ultima" | null {
  if (d.hay === false) return d.motivo === "por_escenas" ? "novela" : null;
  if (enMarcha(d.trabajo) || hayFaseQueLanzar(d)) return null;
  return hayQueAbrirLaUltima(d) ? "ultima" : null;
}
/** Lo que se dice junto a ese botón: con el manuscrito, por qué no se reescribe ahí y qué botón pulsar; con una versión vieja, el aviso de arriba. */
export function fraseDeQueAbrir(d: Reescritura | SinReescritura): string {
  const cual = queAbrir(d);
  if (cual === "novela" && d.hay === false) return porQueNo(d);
  if (cual === "ultima" && d.hay && d.cadena?.ultima) return fraseDeVersion({ hay: true, ultima: d.cadena.ultima });
  return "";
}
/** El motor no encuentra la novela: no se sabe si no se construyó o si se tiró, así que no se da nada por hecho. */
export const FRASE_SIN_NOVELA = "No encuentro la novela construida en la carpeta HumanInk del libro. Si aún no la has construido, hazlo en la pestaña Escritura.";
/**
 * Word no ha abierto la novela o la última versión: dónde está, para que la abra el autor. La misma desde el botón de
 * arriba y desde el «Abrir la última versión» del freno, que no pueden contestar distinto.
 */
export const fraseDeNoSeAbrio = (fichero: string) => `No he podido abrir «${fichero}». Ábrelo tú desde la carpeta HumanInk del libro.`;

const duracion = (minutos: number) => {
  const m = Math.max(1, Math.round(minutos));
  if (m < 60) return veces(m, "minuto", "minutos");
  const h = Math.floor(m / 60), r = m % 60;
  return `${veces(h, "hora", "horas")}${r ? ` y ${veces(r, "minuto", "minutos")}` : ""}`;
};
/** Lo que viaja, dicho antes de gastar. */
export function fraseDeLoQueViaja(c: Cuenta): string {
  if (!c.llamadas) return "Esta fase no llama al modelo (solo quita, o deja indicado dónde escribir): no gasta.";
  return `Viajan unas ${miles(c.palabras)} palabras en ${veces(c.llamadas, "llamada", "llamadas")} al modelo, de tu llave de OpenRouter.`;
}
export function fraseDelTiempo(c: Cuenta): string {
  return c.llamadas ? `Tardará alrededor de ${duracion(c.minutos)}. Puedes cerrar el panel: sigue trabajando.` : "";
}
/** El dinero, con de dónde sale la cifra. Sin de dónde sacarla, se dice que no se sabe: no se inventa. */
export function fraseDelDinero(c: Cuenta): string {
  if (!c.llamadas) return "";
  if (!c.dinero) return "No tengo los precios del modelo, así que no puedo decirte lo que costará.";
  return c.dinero.base === "medido"
    ? `Costará unos ${dinero(c.dinero.usd)}: es lo que salió por palabra en la última fase de este libro.`
    : `Costará unos ${dinero(c.dinero.usd)}, calculado por lo alto: todavía no hay ninguna fase medida en este libro.`;
}
export const rotuloDeLanzar = (d: Reescritura) => `Reescribir la fase ${d.fase?.indice ?? d.fase?.fase ?? ""}${d.fase?.area ? ` · ${d.fase.area}` : ""}`;
export const rotuloDeConfirmar = (c: Cuenta | null | undefined) => (c && !c.llamadas ? "Sí, hacer la fase" : "Sí, gastar y reescribir");

/** El avance de la fase en marcha, entre 0 y 1, y en qué está. */
export const avanceDe = (t: Trabajo) => (t.progreso && t.progreso.total > 0 ? Math.max(0, Math.min(1, t.progreso.hecho / t.progreso.total)) : 0);
export function fraseDeAvance(t: Trabajo): string {
  const paso = (t.pasos ?? []).find((p) => p.estado === "en_curso")?.titulo ?? t.progreso?.fase ?? "";
  const cuantos = t.pasos?.length ? ` (${Math.min((t.progreso?.hecho ?? 0) + 1, t.pasos.length)} de ${t.pasos.length})` : "";
  return paso ? `${paso}${cuantos}` : "Trabajando…";
}

/**
 * La última fase lanzada, si hay algo que decir de ella: lo que dejó (mientras su versión siga siendo la última del
 * libro: después ya es historia) o por qué falló. Varias líneas; vacío si no hay nada que decir.
 */
export function noticiaDeFase(d: Reescritura): { tono: "bien" | "mal"; lineas: string[] } | null {
  const t = d.trabajo;
  if (!t || enMarcha(t)) return null;
  if (t.estado !== "hecho") return { tono: "mal", lineas: [t.error || "La fase no terminó. No se ha creado ninguna versión: vuelve a lanzarla."] };
  const r = t.resultado;
  if (!r?.fichero || d.cadena?.ultima !== r.fichero) return null;
  const aqui = d.fichero === r.fichero;
  const lineas = [
    `Hecho: «${r.fichero}» está en la carpeta HumanInk del libro, con ${veces(r.cambios ?? 0, "cambio marcado", "cambios marcados")}.`
    + (aqui ? "" : r.abierto ? " Se ha abierto en Word." : " Ábrela en Word."),
    "Decide allí con Revisar: cada cambio lleva de revisor su pasada, así que puedes aceptar o rechazar una pasada entera. Guarda, y sigue desde el panel de esa ventana.",
    ...(r.pasadas ?? []).map((x) => `Pasada ${x.n} · ${x.titulo}: ${veces(x.cambios, "cambio", "cambios")}.`),
    ...(r.compartidos ? [`${veces(r.compartidos, "cambio lo han hecho", "cambios los han hecho")} entre varias pasadas: ${r.compartidos === 1 ? "lleva" : "llevan"} de revisor la fase.`] : []),
    ...(r.sin_cambios ?? []).map((x) => `La pasada ${x.n} · ${x.titulo} no proponía ningún cambio.`),
    ...(r.fuera_del_lote ? [`${veces(r.fuera_del_lote, "cambio tocaba", "cambios tocaban")} títulos o separadores de escena y no se ${r.fuera_del_lote === 1 ? "ha" : "han"} escrito: eso no se cambia.`] : []),
    ...(r.sin_tocar?.length ? [`${veces(r.sin_tocar.length, "parte volvió cortada o vacía y se ha dejado", "partes volvieron cortadas o vacías y se han dejado")} como estaba${r.sin_tocar.length === 1 ? "" : "n"}.`] : []),
    ...(r.sin_sitio ?? []).map((x) => `Una operación de la pasada ${x.pasada} no se pudo hacer: ${x.motivo}.`),
    ...(Number(r.coste) > 0 || r.cortadas ? [fraseDeLoGastado(r)] : []),
  ];
  return { tono: "bien", lineas };
}

/**
 * Lo que ha costado la fase. Si alguna llamada se cortó a medias y hubo que repetirla, es «al menos»: OpenRouter puede
 * haber cobrado lo que llevaba y esa parte no trae cuenta.
 */
export function fraseDeLoGastado(r: { coste?: number; cortadas?: number }): string {
  const n = r.cortadas ?? 0, c = dinero(Number(r.coste) || 0);
  if (!n) return `Ha costado ${c}.`;
  return `Ha costado al menos ${c}: ${veces(n, "llamada se cortó a medias y hubo que repetirla", "llamadas se cortaron a medias y hubo que repetirlas")}, y lo que OpenRouter cobrara por ${n === 1 ? "ella" : "ellas"} no viene en esta cuenta.`;
}

export const AVISO_SIN_PREFERENCIAS = "No he podido leer el perfil de la firma de este libro, así que no sé si hay algo que no quieres (poda, cambios de estilo). Hasta que se pueda leer no se reescribe nada.";
export const rotuloDeAprobar = (d: Reescritura) => (d.plan?.aprobado ? "Quitar la aprobación" : "Aprobar el plan");
/**
 * El botón que lleva el relleno, uno solo: aprobar mientras no esté aprobado; después, el de la fase que toca (lanzar,
 * y con el primer clic dado, confirmar); si hay que ir a otra versión, abrirla; y si no queda fase que lanzar y falta
 * leer lo decidido en la versión abierta, leerlo. `pedido`: el primer clic está dado.
 */
export function botonQueToca(m: Momento, d?: Reescritura, pedido = false): "aprobar" | "lanzar" | "confirmar" | "abrir_ultima" | "leer" | null {
  if (m === "por_aprobar") return "aprobar";
  // Sin fase que lanzar (el plan está hecho, o no se puede todavía), lo que toca es leer lo decidido, si falta.
  if (d && hayQueLeer(d) && (m === "terminado" || !hayFaseQueLanzar(d) || !!porQueNoSeLanza(d))) return hayQueAbrirLaUltima(d) ? "abrir_ultima" : "leer";
  if (m !== "aprobado" || !d || !hayFaseQueLanzar(d) || enMarcha(d.trabajo)) return null;
  if (hayQueAbrirLaUltima(d)) return "abrir_ultima";
  if (porQueNoSeLanza(d)) return null;
  return pedido ? "confirmar" : "lanzar";
}

// ── El aviso de «este Word ya no es la última versión», en todas las pestañas (14-oct-2026) ──────────────────────────
// Rais: «al final tienes abiertos un montón de documentos con varios companions… y no sé si el usuario va a entender que
// tiene que abrir el documento y no volver atrás». Hasta hoy solo lo decía la pestaña Reescritura.

/** Lo que dice el motor (`GET /ultima-version`). `hay: false`: este Word no es de una cadena de versiones. */
export type UltimaVersion = { hay: boolean; fichero?: string; ultima?: string | null; es_la_ultima?: boolean };
/** ¿Se avisa? Solo si es seguro que hay otra más nueva y se sabe cuál: ante la duda, no. */
export const hayQueAvisarDeVersion = (u: UltimaVersion | null | undefined): u is UltimaVersion & { ultima: string } =>
  !!u?.hay && u.es_la_ultima === false && typeof u.ultima === "string" && u.ultima.length > 0;
export const fraseDeVersion = (u: UltimaVersion & { ultima: string }) =>
  `Este Word ya no es la última versión de tu libro. La última es «${u.ultima}»: lo que hagas aquí no llega a ella.`;

// ── «Lo que queda sin decidir se da por aceptado» (14-oct-2026) ────────────────────────────────────────────────────────
// Rais: «los cambios que quedan pendientes en el Word se considerarán aceptados, porque no podemos dejar cosas sueltas al
// final». Un clic del autor, con su confirmación: Word los acepta en el documento abierto y lo guarda.
const unos = (n: number) => (n === 1 ? "el cambio que queda" : `los ${n} cambios que quedan`);
export const fraseDeAceptarPendientes = (n: number) =>
  `Si no quieres decidirlos uno a uno, puedo darlos por aceptados: es lo mismo que «Aceptar todos los cambios» de Word, en este documento, y después se guarda.`;
export const rotuloDeAceptarPendientes = (n: number) => (n === 1 ? "Aceptar el que queda y guardar" : `Aceptar los ${n} y guardar`);
export const preguntaDeAceptarPendientes = (n: number) =>
  `¿Acepto ${unos(n)} sin decidir y guardo el documento? Cuentan como aceptados, también en tu certificado. Mientras no cierres el documento, Word lo deshace con Deshacer.`;
export const FRASE_SIN_API_PARA_ACEPTAR = "Este Word no me deja aceptar los cambios desde aquí. Hazlo en Word: Revisar → Aceptar → Aceptar todos los cambios, y guarda.";
export const fraseDeAceptadosSinGuardar = (n: number) =>
  `${n === 1 ? "Aceptado" : `Aceptados los ${n}`} en Word, pero no he podido guardar el documento: guárdalo tú (⌘S en Mac, Ctrl+G en Windows) y vuelve a mirar.`;
export const fraseDeAceptados = (n: number) => (n === 1 ? "Aceptado y guardado." : n ? `Aceptados los ${n} y guardado.` : "No quedaba ningún cambio por decidir. Guardado.");
